import { RequestStatus } from 'common/enums/request-status';
import { RoleGroup } from 'common/enums/role-group';
import { ReducerMethods } from '../../common/interfaces/reducer-methods';
import { MonoliteHelper } from '../../common/monolite';
import { store } from '../../store';
import { CompanySubscriptionUsersChangedModel } from '../account/interfaces/company-subscription-users-changed-model';
import {
  CompanyUserInvitationTokenPayload,
  CompanyUsersPayload,
  UpdateUserRolePayload,
} from './actions/payloads';
import { PeopleActionTypes } from './actions/types';
import { InvitationInfo } from './interfaces/invitation-info';
import { PeopleState } from './interfaces/people-state';
import { UserInfo } from './interfaces/user-info';

export const INITIAL_STATE: PeopleState = {
  filter: '',
  invite_info: null,
  companiesUsers: [],
  usersmap: {},
  displayedRoleGroup: null,
  requests: {
    companiesUsers: RequestStatus.NotRequested,
  },
};

export const peopleReducerMethods: ReducerMethods<PeopleState> = {
  [PeopleActionTypes.COMPANY_PEOPLE_GET_REQUEST]: (s) => {
    return new MonoliteHelper(s)
      .set((_) => _.companiesUsers, [])
      .set((_) => _.requests.companiesUsers, RequestStatus.Loading)
      .get();
  },
  [PeopleActionTypes.COMPANY_PEOPLE_GET_SUCCEEDED]: (state, { people }: CompanyUsersPayload) => {
    return new MonoliteHelper(state)
      .set((_) => _.companiesUsers, people)
      .set((_) => _.requests.companiesUsers, RequestStatus.Loaded)
      .get();
  },
  [PeopleActionTypes.SET_FILTER]: (state, payload: string) => {
    return new MonoliteHelper(state).set((_) => _.filter, payload).get();
  },
  [PeopleActionTypes.INVITE_INFO_GET_SUCCEEDED]: (state, payload: InvitationInfo) => {
    return new MonoliteHelper(state).set((_) => _.invite_info, payload).get();
  },
  [PeopleActionTypes.SAVE_USER_MAP]: (state, payload: Record<string, UserInfo>) => {
    return new MonoliteHelper(state).set((_) => _.usersmap, payload).get();
  },
  [PeopleActionTypes.ADD_TO_USER_MAP]: (state, payload: Record<string, UserInfo>) => {
    return new MonoliteHelper(state)
      .set((_) => _.usersmap, { ...state.usersmap, ...payload })
      .get();
  },
  [PeopleActionTypes.CHANGE_DISPLAYED_ROLE_GROUP]: (state, displayedRoleGroup: RoleGroup | null) => {
    return new MonoliteHelper(state)
      .set((_) => _.displayedRoleGroup, displayedRoleGroup)
      .get();
  },
  [PeopleActionTypes.RESEND_INVITATION_SUCCEEDED]: (
    state,
    { userId }: CompanyUserInvitationTokenPayload,
  ) => {
    if (!state.companiesUsers || !Array.isArray(state.companiesUsers)) {
      return;
    }

    const personIndex = state.companiesUsers.findIndex((p) => p.id === userId);
    if (personIndex === -1) {
      return state;
    }

    return new MonoliteHelper(state)
      .set((_) => _.companiesUsers[personIndex].invitationResent, true)
      .get();
  },
  [PeopleActionTypes.UPDATE_USER_ROLE_SUCCEEDED]: (state, payload: UpdateUserRolePayload) => {
    const { userId, roleId } = payload;
    if (!state.companiesUsers || !Array.isArray(state.companiesUsers)) {
      return;
    }

    const personIndex = state.companiesUsers.findIndex((p) => p.id === userId);
    if (personIndex === -1) {
      return state;
    }

    return new MonoliteHelper(state)
      .set((_) => _.companiesUsers[personIndex].roleId, roleId)
      .get();
  },
  [PeopleActionTypes.DROP_INVITE_INFO]: (state) => {
    return new MonoliteHelper(state)
      .set((_) => _.invite_info, null)
      .get();
  },
  [PeopleActionTypes.UPDATE_SUBSCRIPTION_USERS]: (
    s,
    { companyId, subscriptionType, users }: CompanySubscriptionUsersChangedModel,
  ) => {
    const { account: { selectedCompany, selectedSubscriptionType } } = store.getState();
    const selectedCompanyId = selectedCompany && selectedCompany.id;
    if (selectedCompanyId === companyId && selectedSubscriptionType === subscriptionType) {
      new MonoliteHelper(s)
        .set(_ => _.companiesUsers, users)
        .get();
    }

    return s;
  },
  [PeopleActionTypes.DROP_PEOPLE_STATE]: (state) => {
    return new MonoliteHelper(state)
      .set((_) => _, INITIAL_STATE)
      .get();
  },
};
